import { gameConfig } from './gameConfig.js';

/** Expanding wild (pharaoh). */
export const INFO_WILD_SYMBOL_ID = 9;
/** Coin scatter (Cleopatra) — pays anywhere, multiplied by total bet. */
export const INFO_COIN_SYMBOL_ID = 10;
/** Star scatter (pyramides) — reels 1, 3, and 5; multiplied by total bet. */
export const INFO_SCATTER_SYMBOL_ID = 11;

/** Hub paytable `kind` values (MessagePack StartGameActionResult). */
export const PAYTABLE_KIND = {
  LINE: 0,
  WILD: 1,
  SCATTER: 2,
};

/**
 * @typedef {{ symbol: number, kind: number, minCount: number, odds: number[] }} SessionPaytableEntry
 * @typedef {{ count: number, multiplier: number, fromServer?: boolean, stakeBasis?: 'line' | 'total' }} InfoPayoutSpec
 */

/** @type {SessionPaytableEntry[] | null} */
let sessionPaytable = null;

/**
 * Store server paytable for the current hub session (replaces client copy for info UI).
 * @param {SessionPaytableEntry[] | null | undefined} entries
 */
export function setSessionPaytable(entries) {
  sessionPaytable = Array.isArray(entries) && entries.length ? entries : null;
}

/** @returns {SessionPaytableEntry[] | null} */
export function getSessionPaytable() {
  return sessionPaytable;
}

/**
 * Line-bet multiplier for N consecutive symbols. `payoutMultipliers` is [×2, ×3, ×4, ×5].
 * @param {number[] | undefined} muls
 * @param {number} count matched symbol count (2–5)
 * @returns {number}
 */
export function payoutMultiplierForCount(muls, count) {
  const idx = count - 2;
  if (!Array.isArray(muls) || idx < 0 || idx >= muls.length) return 0;
  return Number(muls[idx]) || 0;
}

/**
 * @param {SessionPaytableEntry} entry
 * @param {number} count
 * @returns {number}
 */
export function serverOddForCount(entry, count) {
  if (!entry || !Array.isArray(entry.odds) || !entry.odds.length) return 0;
  const min = Math.max(0, Number(entry.minCount) || 0);
  if (count < min) return 0;
  const idx = count - min;
  if (idx < 0 || idx >= entry.odds.length) return 0;
  return Number(entry.odds[idx]) || 0;
}

/**
 * @param {import('./gameConfig.js').gameConfig['symbols'][number]} a
 * @param {import('./gameConfig.js').gameConfig['symbols'][number]} b
 */
function compareInfoPaytableSymbolRank(a, b) {
  const mulsA = a.payoutMultipliers;
  const mulsB = b.payoutMultipliers;
  for (let count = 5; count >= 2; count--) {
    const diff = payoutMultiplierForCount(mulsB, count) - payoutMultiplierForCount(mulsA, count);
    if (diff !== 0) return diff;
  }
  return a.id - b.id;
}

/**
 * @param {SessionPaytableEntry} a
 * @param {SessionPaytableEntry} b
 */
function compareSessionPaytableRank(a, b) {
  for (let count = 5; count >= 2; count--) {
    const diff = serverOddForCount(b, count) - serverOddForCount(a, count);
    if (diff !== 0) return diff;
  }
  return a.symbol - b.symbol;
}

/** Active paylines (for rules / layout). Reads live config so 40- and 100-line variants update. */
export function getInfoPaylineCount() {
  return gameConfig.paylines.length;
}

/** Fallback payline symbols when no session paytable yet (high → low). */
export const INFO_PAYTABLE_SYMBOL_IDS = gameConfig.symbols
  .filter((s) => Array.isArray(s.payoutMultipliers) && s.payoutMultipliers.some((m) => Number(m) > 0))
  .sort(compareInfoPaytableSymbolRank)
  .map((s) => s.id);

/**
 * Payline symbol ids for the info grid — prefers server paytable when present.
 * @returns {number[]}
 */
export function getInfoPaytableSymbolIds() {
  if (sessionPaytable?.length) {
    return sessionPaytable
      .filter(
        (e) =>
          e.kind === PAYTABLE_KIND.LINE &&
          Array.isArray(e.odds) &&
          e.odds.some((o) => Number(o) > 0),
      )
      .sort(compareSessionPaytableRank)
      .map((e) => e.symbol);
  }
  return INFO_PAYTABLE_SYMBOL_IDS;
}

/**
 * @param {number} symbolId
 * @returns {import('./gameConfig.js').gameConfig['symbols'][number] | undefined}
 */
export function getSymbolDef(symbolId) {
  return gameConfig.symbols.find((s) => s.id === symbolId);
}

/**
 * @param {number} symbolId
 * @returns {SessionPaytableEntry | undefined}
 */
function sessionEntryForSymbol(symbolId) {
  return sessionPaytable?.find((e) => e.symbol === symbolId);
}

/**
 * @param {SessionPaytableEntry} entry
 * @returns {InfoPayoutSpec[]}
 */
function specsFromSessionEntry(entry) {
  if (!entry || !Array.isArray(entry.odds) || !entry.odds.length) return [];
  const min = Math.max(0, Number(entry.minCount) || 0);
  /** @type {InfoPayoutSpec[]} */
  const out = [];
  for (let i = 0; i < entry.odds.length; i++) {
    const multiplier = Number(entry.odds[i]) || 0;
    if (multiplier <= 0) continue;
    out.push({
      count: min + i,
      multiplier,
      fromServer: true,
      // Hub odds are multiples of the selected bet, not bet ÷ line count.
      stakeBasis: 'total',
    });
  }
  return out.sort((a, b) => b.count - a.count);
}

/**
 * Payline payout rows for the info tab (highest count first).
 * @param {number} symbolId
 * @returns {InfoPayoutSpec[]}
 */
export function getPaylinePayoutSpecs(symbolId) {
  const session = sessionEntryForSymbol(symbolId);
  if (session && session.kind === PAYTABLE_KIND.LINE) {
    return specsFromSessionEntry(session);
  }
  const def = getSymbolDef(symbolId);
  const muls = def?.payoutMultipliers;
  if (!Array.isArray(muls) || !muls.length) return [];
  /** @type {InfoPayoutSpec[]} */
  const out = [];
  for (let count = 5; count >= 2; count--) {
    const multiplier = payoutMultiplierForCount(muls, count);
    if (multiplier <= 0) continue;
    out.push({ count, multiplier, stakeBasis: 'total' });
  }
  return out;
}

/**
 * Scatter payout rows for coin / pyramides columns.
 * @param {number} symbolId
 * @returns {InfoPayoutSpec[]}
 */
export function getScatterPayoutSpecs(symbolId) {
  const session = sessionEntryForSymbol(symbolId);
  if (session && session.kind === PAYTABLE_KIND.SCATTER) {
    return specsFromSessionEntry(session);
  }
  const pays = getSymbolDef(symbolId)?.scatterPays;
  if (!pays || typeof pays !== 'object') return [];
  return Object.keys(pays)
    .map((k) => Number(k))
    .filter((count) => Number.isFinite(count) && count > 0)
    .sort((a, b) => b - a)
    .map((count) => ({
      count,
      multiplier: Number(pays[count]) || 0,
      stakeBasis: 'total',
    }));
}

/**
 * Example win for the info tab.
 * Hub odds are multiples of the selected bet (Crown Crush does not divide by the line count).
 * @param {InfoPayoutSpec} spec
 * @param {{ totalBet: number, lineBet?: number }} stakes
 */
export function payoutAmountForInfoSpec(spec, stakes) {
  const bet = Number(stakes?.totalBet) || Number(stakes?.lineBet) || 0;
  const odd = Number(spec?.multiplier) || 0;
  if (!odd || bet <= 0) return 0;
  return odd * bet;
}
